import type { MapId, PermanentUpgrades } from '../engine/types.js';
import { computeFragments } from '../config/balance.js';

export interface MapRecords {
  bestScore: number;
  bestDay: number;
}

export interface UserProfile {
  username: string | null;
  guestId: string;
  fragments: number;
  upgrades: PermanentUpgrades;
  records: Record<MapId, MapRecords>;
  /** Best day reached on each map for unlocks */
  progressDays: Record<MapId, number>;
  tutorialDone: boolean;
  loggedIn: boolean;
}

const STORAGE_KEY = 'pulsenetwork.profile';

function defaultRecords(): Record<MapId, MapRecords> {
  return {
    core: { bestScore: 0, bestDay: 0 },
    ruins: { bestScore: 0, bestDay: 0 },
    magnetic: { bestScore: 0, bestDay: 0 },
  };
}

export function createGuestProfile(): UserProfile {
  const guestId = `guest_${Math.random().toString(36).slice(2, 10)}`;
  return {
    username: null,
    guestId,
    fragments: 0,
    upgrades: {},
    records: defaultRecords(),
    progressDays: { core: 0, ruins: 0, magnetic: 0 },
    tutorialDone: false,
    loggedIn: false,
  };
}

export function loadProfile(): UserProfile {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return createGuestProfile();
    const parsed = JSON.parse(raw) as UserProfile;
    return { ...createGuestProfile(), ...parsed, records: { ...defaultRecords(), ...parsed.records } };
  } catch {
    return createGuestProfile();
  }
}

export function saveProfile(profile: UserProfile): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
}

export function applyRunFinish(
  profile: UserProfile,
  mapId: MapId,
  day: number,
  score: number,
): { fragmentsEarned: number; profile: UserProfile } {
  const weeks = Math.floor(day / 7);
  const roundedScore = Math.round(score);
  const fragmentsEarned = computeFragments(weeks, roundedScore);
  const next = { ...profile, fragments: profile.fragments + fragmentsEarned };
  const rec = { ...next.records[mapId] };
  if (roundedScore > rec.bestScore) rec.bestScore = roundedScore;
  if (day > rec.bestDay) rec.bestDay = day;
  next.records = { ...next.records, [mapId]: rec };
  next.progressDays = {
    ...next.progressDays,
    [mapId]: Math.max(next.progressDays[mapId] ?? 0, day),
  };
  saveProfile(next);
  return { fragmentsEarned, profile: next };
}
